import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../hooks/useApi';
import Markdown from './Markdown';
import { initialWindowPosition, dragWindowTo, suggestionDraft, initialTab, visibleSuggestions, canArchive, unzoom, keepOnScreen,
  resizeLimits, MIN_WINDOW_SIZE, resizeFromBottomLeft, suggestionCardTint } from './floatingWindow';

// The size it opens at; the bottom-left grip can change it from there.
const WINDOW_SIZE = { width: 420, height: 600 };
const screenSize = () => ({ width: window.innerWidth, height: window.innerHeight });

const STATUS = {
  open:          { label: 'Open',        cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  'in-progress': { label: 'In Progress', cls: 'bg-blue-100  text-blue-700  border-blue-200'  },
  done:          { label: 'Done',        cls: 'bg-green-100 text-green-700 border-green-200' },
  declined:      { label: 'Declined',    cls: 'bg-red-100   text-red-700   border-red-200'   },
};

const PROPOSAL_STATUS = {
  pending:          { label: 'Proposal Pending',    cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  approved:         { label: 'Approved — implementing…', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  declined:         { label: 'Proposal Declined',   cls: 'bg-red-100 text-red-700 border-red-200' },
  awaiting_testing: { label: 'Ready to Test',       cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  implemented:      { label: 'Implemented ✓',       cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

const CATEGORIES = ['UI / UX', 'Performance', 'New Feature', 'Bug', 'Data / Reporting', 'Other'];
const PRIORITIES  = ['low', 'medium', 'high'];

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.open;
  return (
    <span className={`text-[7px] px-1.5 py-0.5 rounded-full border font-bold whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function SuggestionsPanel({ onClose, zoom = 1 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]       = useState(true);

  // The draft outlives the window, so closing it to look at the page (or by
  // accident) does not throw away what has been written.
  const [draft, setDraft] = useState(() => suggestionDraft.load());
  const [tab, setTab]     = useState(() => initialTab(draft));
  const { title, description, category, priority } = draft;
  const updateDraft = (patch) => setDraft(prev => {
    const next = { ...prev, ...patch };
    suggestionDraft.save(next);
    return next;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  // The list shows each suggestion compactly; clicking one opens it in full
  // inside the same window, which is where the archive control lives.
  const [selectedId, setSelectedId]     = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving]       = useState(false);
  const [archiveError, setArchiveError] = useState('');

  // Confirm flow (awaiting_testing)
  const [confirming, setConfirming]     = useState(null);  // suggestion id
  const [confirmNotes, setConfirmNotes] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => {
    api.get('suggestions')
      .then(d => setSuggestions(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // A floating window rather than a slide-over: nothing dims or blocks the page
  // underneath, and the header is a drag handle so it can be moved off whatever
  // the suggestion is about.
  // Everything below is in zoomed units: `zoom` is the header's density zoom.
  const viewport = unzoom(screenSize(), zoom);
  const [pos, setPos] = useState(() => initialWindowPosition(viewport, WINDOW_SIZE));
  // offsetWidth/Height are in the window's own zoomed units, as the maths wants.
  const windowRef = useRef(null);
  const measuredSize = () => ({ width: windowRef.current.offsetWidth, height: windowRef.current.offsetHeight });
  useEffect(() => {
    setPos(p => keepOnScreen(p, measuredSize(), unzoom(screenSize(), zoom)));
  }, [zoom]);
  const dragStart = useRef(null);
  const startDrag = useCallback((e) => {
    if (e.button !== 0 || e.target.closest('button')) return;
    dragStart.current = { pointer: unzoom({ x: e.clientX, y: e.clientY }, zoom), window: pos };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, [pos, zoom]);
  const moveDrag = useCallback((e) => {
    if (!dragStart.current) return;
    setPos(dragWindowTo(dragStart.current, unzoom({ x: e.clientX, y: e.clientY }, zoom), measuredSize(), unzoom(screenSize(), zoom)));
  }, [zoom]);
  const endDrag = useCallback(() => { dragStart.current = null; }, []);

  const [size, setSize] = useState(WINDOW_SIZE);
  const resizeStart = useRef(null);
  const startResize = (e) => {
    if (e.button !== 0) return;
    resizeStart.current = { pointer: unzoom({ x: e.clientX, y: e.clientY }, zoom), window: pos, size: measuredSize() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const moveResize = (e) => {
    if (!resizeStart.current) return;
    const next = resizeFromBottomLeft(resizeStart.current, unzoom({ x: e.clientX, y: e.clientY }, zoom), unzoom(screenSize(), zoom));
    setPos(next.pos);
    setSize(next.size);
  };
  const endResize = () => { resizeStart.current = null; };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) { setError('Title and description are required'); return; }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      const s = await api.post('suggestions', { title, description, category, priority });
      setSuggestions(prev => [s, ...prev]);
      suggestionDraft.clear();
      setDraft(suggestionDraft.load());
      setSuccess('Suggestion submitted — thank you!');
      setTab('mine');
    } catch (e) { setError(e.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleConfirm = async (id, working) => {
    if (!working && !confirmNotes.trim()) { setConfirmError('Please describe the issue'); return; }
    setConfirmLoading(true); setConfirmError('');
    try {
      const updated = await api.put(`suggestions/${id}/confirm`, { working, notes: confirmNotes });
      setSuggestions(prev => prev.map(s => s.id === id ? updated : s));
      setConfirming(null);
      setConfirmNotes('');
    } catch (e) { setConfirmError(e.message || 'Failed'); }
    finally { setConfirmLoading(false); }
  };

  // Archive is the raiser's "read and happy" for a finished ticket: it leaves
  // the list and the bell, and can be brought back from "Show archived".
  const handleArchive = async (id, archive) => {
    setArchiving(true); setArchiveError('');
    try {
      const updated = await api.put(`suggestions/${id}/${archive ? 'archive' : 'unarchive'}`, {});
      setSuggestions(prev => prev.map(s => s.id === id ? updated : s));
      if (archive) setSelectedId(null);
    } catch (e) { setArchiveError(e.message || 'Failed'); }
    finally { setArchiving(false); }
  };

  const awaitingCount = suggestions.filter(s => s.ai_proposal_status === 'awaiting_testing' && !s.archived_at).length;
  const archivedCount = suggestions.filter(s => s.archived_at).length;
  const visible = visibleSuggestions(suggestions, showArchived);
  const selected = suggestions.find(s => s.id === selectedId) || null;
  const limits = resizeLimits(pos, viewport);

  return (
    <div ref={windowRef} className="fixed z-[9000] bg-white shadow-2xl border border-gray-200 rounded-lg flex flex-col"
      style={{ zoom, left: pos.x, top: pos.y, width: size.width, height: size.height,
        minWidth: MIN_WINDOW_SIZE.width, minHeight: MIN_WINDOW_SIZE.height,
        maxWidth: limits.width, maxHeight: limits.height, fontFamily: 'Montserrat, sans-serif' }}
      role="dialog" aria-label="Improvement Suggestions">
        {/* Header: drag handle */}
        <div onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}
          className="flex items-center justify-between px-4 py-3 border-b border-gray-200 cursor-move select-none touch-none rounded-t-lg"
          title="Drag to move">
          <div>
            <div className="text-[11px] font-bold text-gray-900">Improvement Suggestions</div>
            <div className="text-[8px] text-gray-400 mt-0.5">Drag this bar to move the window, or its bottom-left corner to resize it. The page behind stays live.</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-4">
          {[
            ['mine',  awaitingCount > 0 ? `My Suggestions (${awaitingCount} need action)` : 'My Suggestions'],
            ['new',   '+ New Suggestion'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => { setTab(id); setSelectedId(null); setError(''); setSuccess(''); }}
              className={`py-2 px-3 text-[9px] font-semibold border-b-2 -mb-px transition-colors ${
                tab === id ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >{label}</button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">

          {tab === 'mine' && (
            <>
              {loading && <div className="text-[9px] text-gray-400 text-center py-8">Loading…</div>}
              {!loading && !selected && visible.length === 0 && (
                <div className="text-center py-10">
                  <div className="text-2xl mb-2">💡</div>
                  <div className="text-[9px] text-gray-400">{archivedCount > 0 ? 'Nothing live. Your archived suggestions are below.' : 'No suggestions yet.'}</div>
                  <button onClick={() => setTab('new')}
                    className="mt-2 text-[9px] text-blue-600 hover:underline font-semibold">
                    Submit your first idea →
                  </button>
                </div>
              )}
              {/* Detail view: one suggestion in full */}
              {!loading && selected && (
                <div>
                  <button onClick={() => { setSelectedId(null); setArchiveError(''); }}
                    className="text-[8px] text-gray-500 hover:text-gray-800 font-semibold mb-2">
                    ← Back to list
                  </button>
                  <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-[11px] font-bold text-gray-900 flex-1 leading-snug">{selected.title}</div>
                      <StatusBadge status={selected.status} />
                    </div>
                    <Markdown text={selected.description} className="text-[9px] text-gray-700 leading-relaxed" />
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {selected.category && <span className="text-[7px] text-gray-400">{selected.category}</span>}
                      {selected.priority && <span className="text-[7px] text-gray-400 capitalize">{selected.priority} priority</span>}
                      <span className="text-[7px] text-gray-400">Raised {new Date(selected.created_at).toLocaleDateString('en-GB')}</span>
                      {selected.archived_at && <span className="text-[7px] text-gray-400">Archived {new Date(selected.archived_at).toLocaleDateString('en-GB')}</span>}
                    </div>
                    {(() => { const s = selected; return (<>
                {/* Proposal status badge */}
                {s.ai_proposal_status && s.ai_proposal_status !== 'none' && PROPOSAL_STATUS[s.ai_proposal_status] && (
                  <div className="mt-1.5">
                    <span className={`text-[7px] px-1.5 py-0.5 rounded-full border font-bold ${PROPOSAL_STATUS[s.ai_proposal_status].cls}`}>
                      {PROPOSAL_STATUS[s.ai_proposal_status].label}
                    </span>
                  </div>
                )}

                {/* Approved — being implemented */}
                {s.ai_proposal_status === 'approved' && (
                  <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                    <div className="text-[8px] text-emerald-800 font-semibold">Your idea has been approved!</div>
                    <div className="text-[7px] text-emerald-600 mt-0.5">The team is working on implementing it. You'll get an email when it's ready to test.</div>
                  </div>
                )}

                {/* Awaiting testing — action needed */}
                {s.ai_proposal_status === 'awaiting_testing' && (
                  <div className="mt-2 p-2.5 bg-amber-50 border border-amber-300 rounded-lg">
                    <div className="text-[8px] font-bold text-amber-900 mb-0.5">✓ Change implemented — please test!</div>
                    {s.ai_implemented_notes && (
                      <div className="text-[7px] text-amber-800 mb-2 leading-relaxed">{s.ai_implemented_notes}</div>
                    )}
                    {confirmError && confirming === s.id && (
                      <div className="mb-2 text-[7px] text-red-600">{confirmError}</div>
                    )}
                    {confirming === s.id ? (
                      <div>
                        <textarea value={confirmNotes} onChange={e => setConfirmNotes(e.target.value)}
                          placeholder="Describe what's still not working…"
                          rows={2}
                          className="w-full border border-amber-300 rounded px-2 py-1 text-[8px] mb-1.5 focus:outline-none resize-none bg-white" />
                        <div className="flex gap-1.5">
                          <button onClick={() => handleConfirm(s.id, false)}
                            disabled={confirmLoading || !confirmNotes.trim()}
                            className="flex-1 py-1 text-[8px] font-bold bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-50 transition-colors">
                            {confirmLoading ? '…' : 'Report Issue'}
                          </button>
                          <button onClick={() => { setConfirming(null); setConfirmNotes(''); setConfirmError(''); }}
                            className="px-3 py-1 text-[8px] text-gray-500 hover:text-gray-700 border border-gray-200 rounded bg-white">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-1.5 mt-1.5">
                        <button onClick={() => handleConfirm(s.id, true)} disabled={confirmLoading}
                          className="flex-1 py-1.5 text-[8px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded disabled:opacity-50 transition-colors">
                          {confirmLoading ? '…' : '✓ Looks good!'}
                        </button>
                        <button onClick={() => { setConfirming(s.id); setConfirmNotes(''); setConfirmError(''); }}
                          className="flex-1 py-1.5 text-[8px] font-bold border border-red-300 text-red-600 hover:bg-red-50 rounded bg-white transition-colors">
                          Still has issues
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Implemented */}
                {s.ai_proposal_status === 'implemented' && (
                  <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded">
                    <div className="text-[8px] font-bold text-indigo-800 mb-0.5">Implemented ✓</div>
                    {s.ai_implemented_notes && (
                      <div className="text-[7px] text-indigo-700 leading-relaxed">{s.ai_implemented_notes}</div>
                    )}
                  </div>
                )}

                {s.admin_notes && s.ai_proposal_status !== 'awaiting_testing' && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-[7px] font-bold text-blue-700 mb-0.5 uppercase tracking-wide">Update from team</div>
                    <div className="text-[8px] text-blue-800">{s.admin_notes}</div>
                  </div>
                )}
                    </>); })()}
                  </div>

                  {/* Archive / restore */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    {archiveError && <div className="mb-2 text-[8px] text-red-600">{archiveError}</div>}
                    {selected.archived_at ? (
                      <button onClick={() => handleArchive(selected.id, false)} disabled={archiving}
                        className="w-full py-1.5 text-[8px] font-bold border border-gray-300 text-gray-700 hover:bg-gray-100 rounded bg-white disabled:opacity-50 transition-colors">
                        {archiving ? '…' : 'Restore to my list'}
                      </button>
                    ) : canArchive(selected) ? (
                      <>
                        <button onClick={() => handleArchive(selected.id, true)} disabled={archiving}
                          className="w-full py-1.5 text-[8px] font-bold bg-navy text-white hover:bg-navy/90 rounded disabled:opacity-50 transition-colors">
                          {archiving ? '…' : 'Archive this suggestion'}
                        </button>
                        <div className="text-[7px] text-gray-400 mt-1 text-center">Hides it from your list. You can bring it back from “Show archived”.</div>
                      </>
                    ) : (
                      <div className="text-[7px] text-gray-400 text-center">You can archive this once it is marked Done or Declined.</div>
                    )}
                  </div>
                </div>
              )}

              {/* List: compact cards, click to open */}
              {!loading && !selected && visible.map(s => (
                <button key={s.id} onClick={() => setSelectedId(s.id)}
                  className={`w-full text-left mb-3 p-3 border rounded-lg hover:border-navy/40 transition-colors ${suggestionCardTint(s)}${s.archived_at ? ' opacity-70' : ''}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[10px] font-semibold text-gray-900 flex-1 leading-snug">{s.title}</div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-[9px] text-gray-600 leading-relaxed line-clamp-2">{s.description}</div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {s.category && <span className="text-[7px] text-gray-400">{s.category}</span>}
                    <span className="text-[7px] text-gray-300">{new Date(s.created_at).toLocaleDateString('en-GB')}</span>
                    {s.archived_at && <span className="text-[7px] text-gray-400">Archived</span>}
                    {s.ai_proposal_status === 'awaiting_testing' && (
                      <span className="text-[7px] px-1.5 py-0.5 rounded-full border font-bold bg-amber-100 text-amber-700 border-amber-200">Needs your test</span>
                    )}
                    {s.admin_notes && s.ai_proposal_status !== 'awaiting_testing' && (
                      <span className="text-[7px] text-blue-600 font-semibold">Update from team</span>
                    )}
                    <span className="ml-auto text-[7px] text-gray-400">Open →</span>
                  </div>
                </button>
              ))}

              {!loading && !selected && archivedCount > 0 && (
                <button onClick={() => setShowArchived(v => !v)}
                  className="w-full text-center text-[8px] text-gray-500 hover:text-gray-800 font-semibold py-1">
                  {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                </button>
              )}
            </>
          )}

          {tab === 'new' && (
            <div className="flex flex-col gap-3 h-full">
              {error   && <div className="p-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">{error}</div>}
              {success && <div className="p-2 bg-green-50 border border-green-200 rounded text-[9px] text-green-700">{success}</div>}

              <div>
                <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Title *</label>
                <input value={title} onChange={e => updateDraft({ title: e.target.value })}
                  placeholder="Brief summary of your idea"
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400" />
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Description *</label>
                <textarea value={description} onChange={e => updateDraft({ description: e.target.value })}
                  placeholder="Describe your suggestion in detail — what problem does it solve or what would it improve?"
                  rows={5}
                  className="w-full flex-1 min-h-[6rem] border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Category</label>
                  <select value={category} onChange={e => updateDraft({ category: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 bg-white">
                    <option value="">Select…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Priority</label>
                  <select value={priority} onChange={e => updateDraft({ priority: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 bg-white">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <button onClick={handleSubmit} disabled={submitting}
                className="w-full py-2 bg-navy text-white rounded font-bold text-[9px] hover:bg-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {submitting ? 'Submitting…' : 'Submit Suggestion'}
              </button>
            </div>
          )}
        </div>

        {/* Resize grip: bottom-left, so the window grows away from the corner it opens in */}
        <div onPointerDown={startResize} onPointerMove={moveResize} onPointerUp={endResize} onPointerCancel={endResize}
          className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize touch-none text-gray-400 hover:text-gray-600 rounded-bl-lg"
          title="Drag to resize" aria-hidden="true">
          <svg viewBox="0 0 16 16" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M3 5l8 8M3 9l4 4" />
          </svg>
        </div>
    </div>
  );
}
